export class Constants {

  static general = {
    oneMegaInBytes: 1020 * 1024,
    fileSizeLimitInMegaBytes: 25,
    sixMonthsInMillis: 15847200000,
    oneDayInMillis: 86400000,
    notificationsBatchSize: 20,
    minimumNumberOfBoxesValue: 1,
    maximumNumberOfBoxesValue: 1000,
    minimumThirdPartyContractValue: 1,
    maximumThirdPartyContractValue: 500000,
  };

  static styling = {
    darkClassName: 'darkMode'
  };

  static userStatuses: { [status: string]: string } = {
    UNCONFIRMED: 'UNCONFIRMED',
    CONFIRMED: 'CONFIRMED',
    ARCHIVED: 'ARCHIVED',
    COMPROMISED: 'COMPROMISED',
    UNKNOWN: 'UNKNOWN',
    RESET_REQUIRED: 'PASSWORD RESET REQUIRED',
    FORCE_CHANGE_PASSWORD: 'NEVER LOGGED IN'
  };

  static contractTypes = {
    directContract: "directContract",
    partnership: 'partnership'
  };

  static roles = {
    admin: 'admin',
    customer: 'customer',
    researcher: 'researcher',
    logistic: 'logistic',
    tester: 'tester'
  };

  static boxEventTypes = {
    statusChange: 'statusChange',
    boxEnabled: 'boxEnabled',
    boxDisabled: 'boxDisabled',
    researcherAssigned: 'researcherAssigned',
    researcherRemoved: 'researcherRemoved',
    simAssigned: 'simAssigned',
    simRemoved: 'simRemoved',
    certificateAssigned: 'certificateAssigned',
    certificateRemoved: 'certificateRemoved',
  };

  static contractStatuses = {
    new: 'new',
    approvalWaiting: 'approvalWaiting',
    testbedAssociation: 'testbedAssociation',
    rejected: 'rejected',
    boxesWaiting: 'boxesWaiting',
    ongoing: 'ongoing',
    extended: 'extended',
    expired: 'expired',
    closed: 'closed',
    damageReported: 'damageReported',
    finalized: 'finalized'
  };

  static contractStatusesExplainedForLegend = {
    new: 'The contract has just been created.',
    approvalWaiting: 'The contract is waiting for approval from an Administrator.',
    testbedAssociation: 'The contract is waiting for a testbed to be associated.',
    rejected: 'The contract has been rejected from an Administrator.',
    boxesWaiting: 'The contract is waiting for at least one box to be associated.',
    onGoing: 'The contract is ongoing',
    extended: 'The contract has been extended.',
    expired: 'The contract has expired.',
    closed: 'The contract is closed.',
    damageReported: 'A box damage has been reported.',
    finalized: 'The contract is finalized.'
  };

  static boxStatuses = {
    new: 'new',
    onTheShelf: 'onTheShelf',
    broken: 'broken',
    toBeConfigured: 'toBeConfigured',
    assigned: 'assigned',
    shipped: 'shipped',
    delivered: 'delivered',
    mounted: 'mounted',
    available: 'available',
    unavailable: 'unavailable',
    updating: 'updating',
    inUse: 'inUse',
    fail: 'fail',
    toShipBack: 'toShipBack',
    goingHome: 'goingHome',
  };

  static boxStatusesExplainedForLegend = {
    new: 'The box has just been created',
    onTheShelf: 'The box is ready to be assigned to a contract.',
    broken: 'The box is broken.',
    toBeConfigured: 'The box needs to be configured.',
    assigned: 'The box has been assigned to a contract.',
    shipped: `The box has been shipped to customer's`,
    delivered: `The box has been delivered to customer's. It can now be mounted on the testbed.`,
    mounted: 'The box has been mounted on the testbed.',
    available: 'The box is available for use.',
    unavailable: 'The box is not available for use.',
    updating: 'The box is updating.',
    inUse: 'The box is in use',
    fail: `The box has failed at customer's and needs to be shipped back.`,
    toShipBack: 'The box needs to be shipped back.',
    goingHome: 'The box has been shipped back.',
  };

  static simStatuses = {
    unassigned: 'unassigned',
    assigned: 'assigned',
    expired: 'expired',
  };

  static certificateStatuses = {
    unassigned: 'unassigned',
    assigned: 'assigned',
    expired: 'expired',
    revoked: 'revoked'
  };

  static entities = {
    createdBy: 'createdBy',
    contract: 'contract',
    contracts: 'contracts',
    contractEvents: 'contract-events',
    company: 'company',
    companies: 'companies',
    testbed: 'testbed',
    testbeds: 'testbeds',
    storage: 'storage',
    certificate: 'certificate',
    boxCertificates: 'box-certificates',
    connectionLogs: 'connection-logs',
    groupsLogs: 'groupsLogs',
    systemLogs: 'system-logs',
    operationLogs: 'operation-logs',
    interfaceLogs: 'interface-logs',
    updateLogs: 'update-logs',
    resultUpdateLogs: 'result-update-logs',
    tamperingAttemptLogs: 'tampering-attempt-logs',
    researcherCertificates: 'researcher-certificates',
    boxLogisticsStatus: 'logisticsStatus',
    sim: 'sim',
    locations: 'box-locations',
    sims: 'sims',
    box: 'box',
    boxes: 'boxes',
    boxInterface: 'boxInterface',
    boxInterfaces: 'boxInterfaces',
    boxEvents: 'events',
    boxEventsController: 'box-events',
    eventsType: 'eventsType',
    appliedBoxStatus: 'appliedBoxStatus',
    previousBoxStatus: 'previousBoxStatus',
    commonUser: 'common-user',
    admins: 'admins',
    customers: 'customers',
    testers: 'testers',
    researchers: 'researchers',
    logistics: 'logistics',
    notifications: 'notifications',
    executions: 'executions',
    testlist: 'testlist',
    networks: 'networks',
    targets: 'targets',
    input_files: 'input_files',
    uploadSignedUrl: 'uploadSignedUrl',
    image_files: 'image_files',
    users: 'users',
    preferences: 'preferences'
  };

  static snackbarMessages = {
    info: {},
    errors: {
      testbedUnavailable: "Information on this testbed is unavailable"
    },
    success: {}
  }

  static notificationTypes = {
    newCompanyAccountRequest: 'newCompanyAccountRequest',
    newResearcherInvited: 'newResearcherInvited',
    accountActivationChanged: 'accountActivationChanged',
    contractCreated: 'contractCreated',
    contractStatusTransition: 'contractStatusTransition',
    contractAboutToExpire: 'contractAboutToExpire',
    boxRequestToLogistic: 'boxRequestToLogistic', // When in TestbedAssociation, a notification is generated with for LOGISTICS (number of boxes)
    boxCreated: 'boxCreated',
    boxStatusTransition: 'boxStatusTransition',
    newResearcherAssignedToBox: 'newResearcherAssignedToBox',
    researcherRemovedFromBox: 'researcherRemovedFromBox',
    simIsAboutToExpire: 'simIsAboutToExpire',
    certificateIsAboutToExpire: 'certificateIsAboutToExpire',
    testbedDeleted: 'testbedDeleted',
    newDocumentUploaded: 'newDocumentUploaded',
    documentDeleted: 'documentDeleted',
    storageResized: 'storageResized',
    storageDeleted: 'storageDeleted',
  };

  static apiResponses = {
    tokenCorrect: "tokenCorrect",
    tokenIncorrect: "tokenIncorrect",
    tokenExpired: "tokenExpired",
    tokenValid: "tokenValid",
  }

  static rolesConstants(): string[] {
    return Object.values(Constants.roles);
  }

}



